import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import DateCurrentInput from '../inputs/DateCurrentInput';
import RnQpDecisionPanel, { ChecklistItem } from './RnQpDecisionPanel';
import { RnQpThread, rnQpUserLabel } from '../types/rnQpRequests';

const formatDateTime = (value?: string) => (value ? moment(value).format('DD.MM.YYYY HH:mm') : '');
const formatDateOnly = (value?: string | null) => (value && moment(value).isValid() ? moment(value).format('DD.MM.YYYY') : '');

interface Props {
  thread: RnQpThread;

  /** Reply box. */
  replyText: string;
  onReplyTextChange: (value: string) => void;
  replyDateMode: 'accept' | 'new';
  onReplyDateModeChange: (mode: 'accept' | 'new') => void;
  replyDateValue: string;
  onReplyDateValueChange: (value: string) => void;
  onReply: () => void;

  /** QP decision panel. */
  checklist: ChecklistItem[];
  onChecklistToggle: (key: string, checked: boolean) => void;
  pin: string;
  reason: string;
  onPinChange: (value: string) => void;
  onReasonFocus: () => void;
  onReasonChange: (value: string) => void;
  canApprove: boolean;
  canReject: boolean;
  onApprove: () => void;
  onReject: () => void;
}

const RnQpThreadCard: React.FC<Props> = ({
  thread,
  replyText,
  onReplyTextChange,
  replyDateMode,
  onReplyDateModeChange,
  replyDateValue,
  onReplyDateValueChange,
  onReply,
  checklist,
  onChecklistToggle,
  pin,
  reason,
  onPinChange,
  onReasonFocus,
  onReasonChange,
  canApprove,
  canReject,
  onApprove,
  onReject,
}) => {
  const { t } = useTranslation();

  return (
    <div className="rounded-md border border-gray-200">
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold text-blue-700">{thread.number}</div>
          <div className="text-xs font-semibold text-gray-800">{thread.subject}</div>
        </div>
        <div className="text-[10px] text-gray-600">
          {t('rnQpRequests.dueDate')}: {formatDateOnly(thread.dateTo) || '—'}
        </div>
      </div>

      <div className="space-y-2 px-3 py-2">
        {thread.messages.map((msg) => (
          <div key={msg._id} className="rounded bg-gray-50 px-2 py-1.5 text-xs">
            <div className="flex justify-between text-[10px] text-gray-500">
              <span className="font-semibold text-gray-700">{msg.authorName || rnQpUserLabel(msg.author)}</span>
              <span>{t(`rnQpRequests.kind_${msg.kind}`)} · {formatDateTime(msg.createdAt)}</span>
            </div>
            <div className="mt-0.5 whitespace-pre-wrap text-gray-800">{msg.text}</div>
          </div>
        ))}
      </div>

      {thread.status !== 'closed' && (
        <div className="space-y-2 border-t border-gray-200 px-3 py-2">
          <textarea
            value={replyText}
            onChange={(e) => onReplyTextChange(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:outline-none focus:bg-blue-50 focus:ring-2 focus:ring-blue-500/50"
            placeholder={t('rnQpRequests.reply')}
          />
          <div className="flex flex-wrap items-center gap-2 text-[10px]">
            <button
              type="button"
              onClick={() => onReplyDateModeChange('accept')}
              className={`rounded border px-2 py-1 ${
                (replyDateMode || 'accept') === 'accept' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600'
              }`}
            >
              {t('rnQpRequests.acceptDeadline')}
            </button>
            <button
              type="button"
              onClick={() => onReplyDateModeChange('new')}
              className={`rounded border px-2 py-1 ${
                replyDateMode === 'new' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600'
              }`}
            >
              {t('rnQpRequests.newDeadline')}
            </button>
            {replyDateMode === 'new' && (
              <DateCurrentInput
                value={replyDateValue}
                onChange={onReplyDateValueChange}
                showIcon
                inputClassName="w-36 rounded border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:bg-blue-50 focus:ring-2 focus:ring-blue-500/50"
              />
            )}
            <button
              type="button"
              onClick={onReply}
              className="ml-auto rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
            >
              {t('rnQpRequests.reply')}
            </button>
          </div>
        </div>
      )}

      <RnQpDecisionPanel
        decision={thread.decision}
        releaseTransfer={thread.releaseTransfer}
        checklist={checklist}
        onChecklistToggle={onChecklistToggle}
        pin={pin}
        reason={reason}
        onPinChange={onPinChange}
        onReasonFocus={onReasonFocus}
        onReasonChange={onReasonChange}
        canApprove={canApprove}
        canReject={canReject}
        onApprove={onApprove}
        onReject={onReject}
        formatDateTime={formatDateTime}
      />
    </div>
  );
};

export default RnQpThreadCard;
